import { useState, useEffect, useRef } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Select } from '@/components/ui/Select'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { Mail, Send, X, FileText, Paperclip, Trash2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import type { User } from '@/types/database-real'
import { emailService } from '@/services/email'
import { createCustomEmail } from '@/utils/emailTemplates'

interface Attachment {
  file: File
  filename: string
  content: string // base64
  content_type: string
}

const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024 // 10 MB par fichier
const MAX_TOTAL_SIZE = 25 * 1024 * 1024 // 25 MB total

interface EmailTemplate {
  id: string
  name: string
  subject: string
  body_html: string
  category: string
}

interface SendEmailModalProps {
  isOpen: boolean
  onClose: () => void
  client: User
  projectId?: string
}

export function SendEmailModal({ isOpen, onClose, client, projectId }: SendEmailModalProps) {
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Charger les templates d'email
  useEffect(() => {
    if (isOpen) {
      loadEmailTemplates()
    }
  }, [isOpen])

  async function loadEmailTemplates() {
    try {
      setLoadingTemplates(true)
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('is_active', true)
        .order('name')

      if (error) throw error
      setTemplates(data || [])
    } catch (err) {
      console.error('Error loading email templates:', err)
    } finally {
      setLoadingTemplates(false)
    }
  }

  // Appliquer un template
  function applyTemplate(templateId: string) {
    const template = templates.find((t) => t.id === templateId)
    if (template) {
      setSubject(template.subject)
      setBody(stripHtmlTags(template.body_html))
      setSelectedTemplate(templateId)
    }
  }

  // Supprimer les balises HTML pour l'affichage dans le textarea
  function stripHtmlTags(html: string): string {
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent || div.innerText || ''
  }

  // Remplacer les variables dans le template
  function replaceVariables(text: string): string {
    return text
      .replace(/\{client_name\}/g, client.full_name || 'Client')
      .replace(/\{client_email\}/g, client.email || '')
      .replace(/\{client_company\}/g, client.company || '')
  }

  // Gestion des pièces jointes
  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        // Extraire le contenu base64 sans le préfixe data:...;base64,
        const base64 = result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files
    if (!files?.length) return

    const currentTotalSize = attachments.reduce((sum, a) => sum + a.file.size, 0)
    const newAttachments: Attachment[] = []

    for (const file of Array.from(files)) {
      if (file.size > MAX_ATTACHMENT_SIZE) {
        setError(`"${file.name}" dépasse la taille maximale de 10 MB`)
        continue
      }
      if (currentTotalSize + file.size > MAX_TOTAL_SIZE) {
        setError('Taille totale des pièces jointes limitée à 25 MB')
        break
      }
      if (attachments.some((a) => a.filename === file.name)) {
        continue // skip duplicates
      }

      const content = await fileToBase64(file)
      newAttachments.push({
        file,
        filename: file.name,
        content,
        content_type: file.type || 'application/octet-stream',
      })
    }

    setAttachments((prev) => [...prev, ...newAttachments])
    // Reset input pour permettre de re-sélectionner le même fichier
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function removeAttachment(filename: string) {
    setAttachments((prev) => prev.filter((a) => a.filename !== filename))
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} o`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`
  }

  // Envoyer l'email
  async function handleSendEmail() {
    try {
      setSending(true)
      setError(null)
      setSuccess(false)

      // Validation
      if (!subject.trim()) {
        throw new Error('Le sujet est requis')
      }
      if (!body.trim()) {
        throw new Error('Le message est requis')
      }
      if (!client.email) {
        throw new Error('Le client n\'a pas d\'adresse email')
      }

      // Remplacer les variables
      const finalSubject = replaceVariables(subject)
      const finalBody = replaceVariables(body)

      // Générer le HTML avec le template
      const htmlContent = createCustomEmail({
        clientName: client.full_name || 'Client',
        subject: finalSubject,
        message: finalBody,
      })

      // Préparer les pièces jointes
      const emailAttachments = attachments.length > 0
        ? attachments.map((a) => ({
            filename: a.filename,
            content: a.content,
            content_type: a.content_type,
          }))
        : undefined

      // Envoyer l'email via le service email
      const emailRequest = {
        to: client.email,
        toName: client.full_name || undefined,
        subject: finalSubject,
        html: htmlContent,
        text: finalBody,
        referenceType: 'client' as const,
        referenceId: client.id,
        attachments: emailAttachments,
      }

      // Envoyer à tous les membres du projet si projectId est fourni
      let emailResult
      if (projectId) {
        const { mainResult } = await emailService.sendEmailToProject(projectId, emailRequest)
        emailResult = mainResult
      } else {
        emailResult = await emailService.sendEmail(emailRequest)
      }

      if (!emailResult.success) {
        throw new Error(emailResult.error || 'Erreur lors de l\'envoi de l\'email')
      }

      // Créer un log d'email
      const { error: logError } = await supabase
        .from('email_history')
        .insert({
          to_email: client.email,
          to_name: client.full_name || null,
          subject: finalSubject,
          content: htmlContent,
          status: 'sent',
          related_type: 'client',
          related_id: client.id,
          template_id: selectedTemplate || null,
        })

      if (logError) {
        console.error('Erreur lors de la création du log:', logError)
        // Ne pas faire échouer l'envoi si le log échoue
      }

      setSuccess(true)
      setTimeout(() => {
        handleClose()
      }, 2000)
    } catch (err) {
      console.error('Error sending email:', err)
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'envoi')
    } finally {
      setSending(false)
    }
  }

  function handleClose() {
    setSubject('')
    setBody('')
    setSelectedTemplate('')
    setAttachments([])
    setError(null)
    setSuccess(false)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-mint-50 rounded-lg">
              <Mail className="w-5 h-5 text-mint-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-brown-900">
                Envoyer un email
              </h2>
              <p className="text-sm text-brown-500">
                À : {client.full_name || client.email}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-brown-400 hover:text-brown-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Template selector */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-brown-700 mb-2">
            <FileText className="w-4 h-4 inline mr-1" />
            Template (optionnel)
          </label>
          {loadingTemplates ? (
            <div className="flex items-center gap-2 text-brown-500">
              <Spinner size="sm" />
              <span className="text-sm">Chargement des templates...</span>
            </div>
          ) : (
            <Select
              value={selectedTemplate}
              onChange={(e) => applyTemplate(e.target.value)}
            >
              <option value="">Choisir un template...</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </Select>
          )}
        </div>

        {/* Recipient info */}
        <div className="mb-4 p-3 bg-beige-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-brown-500">Destinataire</p>
              <p className="font-medium text-brown-900">{client.email}</p>
            </div>
            <Badge variant="mint">Client</Badge>
          </div>
        </div>

        {/* Subject */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-brown-700 mb-2">
            Sujet *
          </label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Objet de l'email"
            disabled={sending}
          />
        </div>

        {/* Body */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-brown-700 mb-2">
            Message *
          </label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Écrivez votre message ici...&#10;&#10;Variables disponibles :&#10;{client_name}, {client_email}, {client_company}"
            rows={10}
            disabled={sending}
            className="font-mono text-sm"
          />
          <p className="mt-2 text-xs text-brown-500">
            Utilisez {'{client_name}'}, {'{client_email}'}, {'{client_company}'} pour personnaliser
          </p>
        </div>

        {/* Pièces jointes */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-brown-700 mb-2">
            <Paperclip className="w-4 h-4 inline mr-1" />
            Pièces jointes
          </label>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            disabled={sending}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            className="w-full border-2 border-dashed border-beige-300 rounded-lg p-4 text-center hover:border-mint-400 hover:bg-mint-50/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Paperclip className="w-5 h-5 mx-auto mb-1 text-brown-400" />
            <p className="text-sm text-brown-500">
              Cliquez pour ajouter des fichiers
            </p>
            <p className="text-xs text-brown-400 mt-1">
              Max 10 Mo par fichier, 25 Mo au total
            </p>
          </button>

          {attachments.length > 0 && (
            <div className="mt-3 space-y-2">
              {attachments.map((attachment) => (
                <div
                  key={attachment.filename}
                  className="flex items-center justify-between p-2 bg-beige-50 rounded-lg"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Paperclip className="w-4 h-4 text-brown-400 flex-shrink-0" />
                    <span className="text-sm text-brown-900 truncate">
                      {attachment.filename}
                    </span>
                    <span className="text-xs text-brown-400 flex-shrink-0">
                      ({formatFileSize(attachment.file.size)})
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(attachment.filename)}
                    disabled={sending}
                    className="p-1 text-brown-400 hover:text-red-500 transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <p className="text-xs text-brown-400 text-right">
                {attachments.length} fichier{attachments.length > 1 ? 's' : ''} —{' '}
                {formatFileSize(attachments.reduce((sum, a) => sum + a.file.size, 0))}
              </p>
            </div>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Success message */}
        {success && (
          <div className="mb-4 p-3 bg-mint-50 border border-mint-200 rounded-lg">
            <p className="text-sm text-mint-700">
              ✓ Email envoyé avec succès !
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={sending}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSendEmail}
            disabled={sending || !subject.trim() || !body.trim()}
            leftIcon={sending ? <Spinner size="sm" /> : <Send className="w-4 h-4" />}
          >
            {sending ? 'Envoi en cours...' : 'Envoyer'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
